// Shop sensors (/sensors): the compressor shed's temperature, and any sensor after it (jobq-api
// "SHOP ENVIRONMENT SENSORS", since 2026-10-26). The alert pushes link here, so the route is exactly
// /sensors and is NOT behind the admin unlock: anyone who gets the alert can see the temperature.
// A card per sensor, the picked sensor's chart + alert history, and an Admin section (settings, test
// alert, token, register) behind the usual Admin PIN. GET /sensors is re-read every 60 s while the
// tab is visible. Words + rules: features/sensors/sensorRules.js. The phone layout is
// mobile/screens/SensorsScreen.jsx on the same pieces.

import React, { useRef, useState } from 'react';
import SensorCard from '../components/sensors/SensorCard';
import SensorHistory from '../components/sensors/SensorHistory';
import SensorAdmin from '../components/sensors/SensorAdmin';
import { useSensorList } from '../features/sensors/useSensors';
import { pickSensor } from '../features/sensors/sensorRules';

const styles = {
  page: { maxWidth: 1100 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 },
  small: { fontSize: 13 },
};

export default function SensorsPage() {
  const { sensors, error, reload } = useSensorList();
  const [wantedId, setWantedId] = useState(null);
  const historyReload = useRef(null);
  const selected = pickSensor(sensors, wantedId);
  const stamp = selected ? `${selected.last_reading_at || ''}|${selected.last_alert_at || ''}` : '';

  const changed = () => {
    reload();
    if (historyReload.current) historyReload.current();
  };

  return (
    <div className="container py-4" style={styles.page} data-testid="sensors-page">
      <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-1">
        <h2 className="mb-0">Shop sensors</h2>
        <span className="text-muted" style={styles.small}>Re-read every minute</span>
      </div>
      <p className="text-muted mb-3" style={styles.small}>
        Each board reports once a minute. JobQ alerts when three readings in a row are past a limit, and when a
        board stops reporting.
      </p>

      {error && (
        <div className="alert alert-warning py-2" role="alert">
          {sensors && sensors.length ? `Showing the last answer: ${error}` : error}
        </div>
      )}
      {sensors === null && !error && <div className="text-muted">Loading…</div>}
      {sensors && sensors.length === 0 && !error && (
        <div className="text-muted mb-3">No sensors are registered yet. An Admin registers one below.</div>
      )}

      {sensors && sensors.length > 0 && (
        <>
          <div style={styles.grid} className="mb-4" data-testid="sensor-cards">
            {sensors.map((s) => (
              <SensorCard key={s.sensor_id} sensor={s} selected={selected && selected.sensor_id === s.sensor_id}
                          onSelect={sensors.length > 1 ? setWantedId : undefined} />
            ))}
          </div>
          <div className="card mb-3">
            <div className="card-body">
              <SensorHistory sensor={selected} stamp={stamp} reloadRef={historyReload} />
            </div>
          </div>
        </>
      )}

      {sensors !== null && (
        <SensorAdmin sensor={selected} onChanged={changed} onCreated={(id) => setWantedId(id)} />
      )}
    </div>
  );
}
